import { AuditOutlined } from '@ant-design/icons';
import { Card, Form, Input, Select, Typography } from 'antd';
import { EditLayout } from 'components/layout/layout/EditLayout';
import { ICertifiers } from 'interfaces/certifiers';
import { ISignaturePartners } from 'interfaces/signature_partners';
import { ISignatures } from 'interfaces/signatures';
import { PageHeader } from 'components/professor_area/PageHeader';
import {
  CERTIFICATE_REPORT_KIND,
  signaturePartnerErrorNotification,
  signaturePartnerReportKindOptions
} from 'services/signature-partners';
import { useCallback } from 'react';
import { useForm, useSelect } from '@refinedev/antd';

export const SignaturePartnersEdit = () => {
  const { form, formProps, saveButtonProps, onFinish } =
    useForm<ISignaturePartners>({
      successNotification: () => ({
        type: 'success',
        description: 'Sucesso!',
        message: 'Modelo de assinatura editado com sucesso!'
      }),
      errorNotification: error =>
        signaturePartnerErrorNotification(
          error,
          'Não foi possível editar o modelo de assinatura!'
        )
    });
  const certifierId = Form.useWatch('certifier_id', form);
  const kind = Form.useWatch('report_kind', form);

  const { selectProps: selectCertifiers } = useSelect<ICertifiers>({
    resource: 'certifiers',
    optionLabel: 'name',
    optionValue: 'id',
    fetchSize: 100,
    liveMode: 'manual',
    pagination: {
      mode: 'server'
    }
  });
  const { selectProps: selectSignatures } = useSelect<ISignatures>({
    resource: 'certifiers/signatures',
    filters: [
      {
        field: 'by_certifier_id',
        operator: 'eq',
        value: certifierId
      }
    ],
    optionLabel: 'name',
    optionValue: 'id',
    fetchSize: 100,
    liveMode: 'manual',
    pagination: {
      mode: 'server'
    }
  });

  const handleFinish = useCallback(() => {
    onFinish({
      ...form.getFieldsValue(),
      signatures: {
        front_signature_id: form.getFieldValue('front_signature_id'),
        back_signature_id: form.getFieldValue('back_signature_id'),
        record_back_signature_id: form.getFieldValue('record_back_signature_id')
      }
    }).catch(error => {
      // eslint-disable-next-line no-console
      console.error(error);
    });
  }, [form, onFinish]);

  const handleKindChange = useCallback(() => {
    form.setFieldValue('front_signature_id', null);
    form.setFieldValue('back_signature_id', null);
    form.setFieldValue('record_back_signature_id', null);
  }, [form]);

  return (
    <EditLayout saveButtonProps={saveButtonProps}>
      <PageHeader
        title={formProps?.initialValues?.name || 'Editar modelo de assinatura'}
        icon={AuditOutlined}
      />
      <Card>
        <Form
          {...formProps}
          initialValues={{
            ...formProps.initialValues,
            ...formProps?.initialValues?.signatures
          }}
          onFinish={handleFinish}
          layout="vertical"
        >
          <Form.Item
            label="Nome do modelo"
            name="name"
            rules={[
              {
                required: true,
                message: 'Nome do modelo obrigatório'
              }
            ]}
          >
            <Input className="max-w-[500px]" />
          </Form.Item>
          <Form.Item
            label="Nome da instituição"
            name="certifier_id"
            rules={[
              {
                required: true,
                message: 'Nome da instituição obrigatório'
              }
            ]}
          >
            <Select
              {...selectCertifiers}
              allowClear
              style={{ minWidth: 100 }}
              disabled
            />
          </Form.Item>
          <Form.Item
            label="Local"
            name="report_kind"
            rules={[
              {
                required: true,
                message: 'Local obrigatório'
              }
            ]}
          >
            <Select
              allowClear
              style={{ minWidth: 100 }}
              onChange={handleKindChange}
            >
              {signaturePartnerReportKindOptions.map(({ value, label }) => (
                <Select.Option key={value} value={value}>
                  {label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Typography.Title level={5} style={{ margin: '24px 0' }}>
            Assinaturas
          </Typography.Title>
          <Form.Item
            label="Assinatura Frente"
            name="front_signature_id"
            rules={[
              {
                required: true,
                message: 'Nome da instituição obrigatório'
              }
            ]}
          >
            <Select
              {...selectSignatures}
              allowClear
              style={{ minWidth: 100 }}
            />
          </Form.Item>
          {kind === CERTIFICATE_REPORT_KIND && (
            <>
              <Form.Item
                label="Assinatura Verso"
                name="back_signature_id"
                rules={[
                  {
                    required: true,
                    message: 'Nome da instituição obrigatório'
                  }
                ]}
              >
                <Select
                  {...selectSignatures}
                  allowClear
                  style={{ minWidth: 100 }}
                />
              </Form.Item>
              <Form.Item
                label="Assinatura Verso - Registro"
                name="record_back_signature_id"
                rules={[
                  {
                    required: true,
                    message: 'Nome da instituição obrigatório'
                  }
                ]}
              >
                <Select
                  {...selectSignatures}
                  allowClear
                  style={{ minWidth: 100 }}
                />
              </Form.Item>
            </>
          )}
        </Form>
      </Card>
    </EditLayout>
  );
};
